import React from 'react'
import { useParams } from 'react-router'
import get from 'lodash/get'
import styled from 'styled-components'
import { isMobile } from 'react-device-detect'
import { useCertificate, useQueryParamLang, useLanguage } from '../../hooks'
import ImageBackground from '../../components/common/ImageBackground'
import Certificate from '../../components/achievements/certificate'
import Share from '../../components/social/share'
import SplitFlapDisplay from '../../components/common/SplitFlapDisplay'
import Button from '../../components/common/Button'
import Column from '../../components/common/Column'
import Text from '../../components/common/Text'
import Icon from '../../components/common/Icon'
import Block from '../../components/common/Block'
import Spacer from '../../components/common/Spacer'
import { theme, setSize } from '../../style'

const {
    REACT_APP_FIREBASE_PROJECT_ID,
} = process.env

const MainContainer = styled.div`
    display: flex;
    justify-content: center;
    background-color: ${theme.darkSlateBlue};
`

const AchievementsWidget = () => {

    const { lang } = useQueryParamLang()
    const { t } = useLanguage(lang)

    // (cid) certificateID used to retrieve the info on DB for this achievements certificate
    // (uid) userID used to match the one present on certificate document to authenticate
    // that this link is from user email => show download cert. PDF button
    const { cid, uid, date } = useParams()

    const [loadingInfo, error, certificateInfo] = useCertificate(cid, date)

    if (loadingInfo) {
        return (
            // TODO: add a nice loading component to be reused across widgets
            <Block alignItems="center">
                <Text>{t('loadingComp.title')}</Text>
            </Block>
        )
    }

    if (error) {
        return null
    }

    const {
        userID,
        treesPlantedMonthly,
        timestamp,
        nickname,
    } = certificateInfo

    const isAuth = userID === uid

    // eslint-disable-next-line max-len
    const downloadPdfUrl = `https://us-central1-${REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/getCertificatePdf?date=${date}&cid=${cid}&lang=${lang}`

    const shareContent = {
        title: 'Tribaldata',
        message: t('achievementsWidget.shareContent.message'),
        subject: t('achievementsWidget.shareContent.subject'),
        url: window.location.href.replace(`/${userID}`, ''),
        // If userID param is present in the url remove it before sharing it
    }

    return (
        <MainContainer>
            <Column
                width={isMobile ? '100%' : '60%'}
                bgColor="white"
            >
                <Block pv="XXXXL" alignItems="center">
                    <Icon
                        iconName="logoTents@2x.png"
                        width={60}
                        height="auto"
                        alignSelf="center"
                    />
                    <Spacer size="XL" />
                    <Text uppercase size="S" semibold>
                        {t('achievementsWidget.myAchievements.title')}
                    </Text>
                </Block>

                <ImageBackground
                    width="100%"
                    height={isMobile ? '90px' : '12vh'}
                    imageName="cloudsHeader@2x.png"
                />

                <Block
                    pt="Z"
                    pb="XXL"
                    ph="XXL"
                    bgColor="blueLight3"
                    alignItems="center"
                    style={{ marginTop: '-1px' }}
                >
                    <Text uppercase size="S" semibold>
                        {t('achievementsWidget.plantedTress.title')}
                    </Text>
                    <Spacer size="L" />
                    <Text size="L">
                        Madagascar
                    </Text>
                    <Spacer size="L" />
                    <SplitFlapDisplay
                        number={treesPlantedMonthly}
                        length={6}
                        size={setSize('S', 'XS')}
                        alignSelf="center"
                    />

                    <Spacer size="G" />

                    <Certificate
                        treesPlantedMonthly={treesPlantedMonthly}
                        nickname={nickname}
                        id={cid}
                        // eslint-disable-next-line no-underscore-dangle
                        date={get(timestamp, '_seconds', 0) * 1000}
                        width={isMobile ? '90%' : '75%'}
                        treePluralTranslated={t('achievementsWidget.tree', { count: treesPlantedMonthly })}
                        language={lang}
                    />

                    {isAuth && (
                        <Block pt="G" pb="XXL" alignItems="center">
                            <Button
                                target="_blank"
                                tag="a"
                                href={downloadPdfUrl}
                                variant="info"
                                width={300}
                                title={t('achievementsWidget.downloadCert.title')}
                            />
                        </Block>
                    )}
                </Block>

                <Block
                    pv={isMobile ? 'S' : 'XXXL'}
                    ph="L"
                    bgColor="blue"
                    alignItems="center"
                >
                    <Spacer size="M" />
                    <Text uppercase size="XS" semibold color="white" space="L">
                        {t('achievementsWidget.shareIt.title')}
                    </Text>
                    <Spacer size="M" />
                    <Share shareContent={shareContent} />
                </Block>
            </Column>
        </MainContainer>
    )
}

export default AchievementsWidget
